import { UserPersonalData } from '../authentication.service';
import {
  AIR_POLLUTION_INDEX,
  AVERAGE_TRAVEL_TIME_TO_WORK,
  BACHELORS_DEGREE_OR_HIGHER_FRACTION,
  BIDEN_VS_TRUMP_2020_POPULATION,
  COMBINED_HWW_BY_COUNTRY,
  COMBINED_HWW_BY_STATE,
  COMBINED_HWW_CONST,
  CRIME_RATE,
  FAMILY_POI_CHART,
  GINI_INDEX,
  HARRIS_VS_TRUMP_2024_POPULATION,
  HEALTHCARE_POI_CHART,
  HEALTH_INDEX_BY_COUNTRY,
  HEALTH_INDEX_BY_STATE,
  HEALTH_INDEX_CONST,
  HOME_OWNERSHIP_RATIO,
  HOME_PRICE_TO_INCOME_RATIO,
  HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS,
  LIFE_EXPECTANCY,
  MARITAL_STATUS_CHART,
  MEDIAN_AGE,
  MEDIAN_HOUSEHOLD_INCOME_TREND,
  MEDIAN_HOUSING_SALE_COST_TREND,
  MEDIAN_INCOME,
  PATIENT_TO_PHYSICIAN_RATIO,
  POPULATION,
  POPULATION_GROWTH_TREND,
  PRESIDENTIAL_ELECTIONS_RESULTS2020,
  PRESIDENTIAL_ELECTIONS_RESULTS2024,
  RACE_DISTRIBUTION_CHART,
  SENATE_ELECTION_2022_RESULTS,
  SMOKING_RATE,
  TRAFFIC_INTENSITY_CHART,
  WEALTH_INDEX_BY_COUNTRY,
  WEALTH_INDEX_BY_STATE,
  WEALTH_INDEX_CONST,
  WISE_INDEX_BY_COUNTRY,
  WISE_INDEX_BY_STATE,
  WISE_INDEX_CONST,
} from '../../shared/types/feature-data-type';
import { FeatureType } from '../access/user-access.service';

export const COMMUNITY_COMPASS_REPORT_SAMPLE =
  'https://cdn.prod.website-files.com/645fc9b1f2ae787ad9542f5d/682da5adc3563c4e2addf60f_Community%20Compass.pdf';
export const OPEN_HOUSE_FLYER_REPORT_SAMPLE =
  'https://uploads-ssl.webflow.com/645fc9b1f2ae787ad9542f5d/66bddfdbe2857c83cbe388d3_flyer.pdf';
export const COMPARATIVE_LOCATION_ANALYSIS_REPORT_SAMPLE =
  'https://cdn.prod.website-files.com/645fc9b1f2ae787ad9542f5d/6624318434fe9596295ad508_Comparative%20Location%20Analysis.pdf';
// Business Site Traffic. The enum key stays LOCATION_INTELLIGENCE - that value
// is the backend's `ReportType`, only the display name changed.
export const LOCATION_INTELLIGENCE_REPORT_SAMPLE =
  'https://cdn.prod.website-files.com/632245fb04b2505bff6aa615/688b7808e90febc7a2c00912_CS%20Comp.pdf';
export const FEASIBILITY_STUDY_REPORT_SAMPLE =
  'https://cdn.prod.website-files.com/645fc9b1f2ae787ad9542f5d/672bb82c544c994dcb5c4f12_Feasibility%20study.pdf';

export interface IndicatorsDescription {
  description: string;
  groups: GroupDescription[];
}

export interface GroupDescription {
  groupTitle: string;
  bulletList: string[];
}

type ReportDescription = {
  reportsIncludedDescription?: string;
  reportsPaidDescription?: string;
  includedInSubscription?: boolean;
  isReadyToOrder: boolean;
  sampleURL?: string;
};

export type ReportTypeDescription = {
  reportsBase?: ReportDescription[];
  reportsMonthly?: ReportDescription[];
  reportsYearly?: ReportDescription[];
};

export type PlainTextFeatureDescription = string;

export interface SubscriptionPlanDescription {
  planName: string;
  preformattedPlanName?: string;
  priceMonthAnnually?: string;
  audience?: string;
  // price for package subscriptions which are 1-time purchases
  pricePackage?: string;
  // price for yearly subscription
  priceYearly?: string;
  priceMonthly?: string;
  priceLifetime?: string;
  yearlyDiscountPercents?: string;
  reportSample?: string;
  includesAi?: boolean;
  aiTokensIncludedMonthlyText: string;
  aiTokensIncludedYearlyText: string;
  requestKey: SubscriptionPlans;
  accessibleFeaturesDescription: {
    reportTypesDescription: ReportTypeDescription;
    plainTextFeatureDescription?: PlainTextFeatureDescription[];
  };
  inaccessibleReportTypes?: ReportTypes[];
  accessibleReportTypes?: ReportTypes[] | null;
  // If plan is not meant to be seen by user
  notAvailableForUser?: boolean;
  // Plan can be shown but not purchased (shows "Contact us" instead of a price/button)
  contactUsOnly?: boolean;
  /**
   * Sold with a coverage region attached: `POST /user/subscription` rejects
   * these plans without `regionType` + `regionsIdsCommaSeparated`, and the
   * backend picks the Stripe price from that pair.
   *
   * Every purchase now sends the whole country (`US_REGION`) - there is no
   * coverage-area step and no county/state tier any more - so `priceMonthly`
   * above is the nationwide price and the only one this plan is sold at.
   */
  requiresRegion?: boolean;
}

export interface TrialDescription {
  planName: string;
  accessibleFeatures: FeatureType[];
  accessibleReportTypes: ReportTypes[];
  accessibleFeaturesText: string[];
}

export interface UnauthorizedDescription {
  accessibleFeatures: FeatureType[];
}

/**
 * Types of Map AI subscription user can choose from.
 * <a href="https://docs.google.com/spreadsheets/d/10mwuPckbuoKjwAwojio7mOz0VJ0N0OXI7m0tj7wM7C8/edit?usp=sharing">Google doc</a>
 */
export enum SubscriptionPlans {
  BASIC = 'BASIC',
  PLUS = 'PLUS',
  PRO = 'PRO',
  /**
   * Cheapest: some data for states & counties only.
   */
  KNOW_YOUR_COUNTRY = 'KNOW_YOUR_COUNTRY',
  /**
   * Individual subscriptions: Cells and some data added.
   */
  COMMUNITY_COMPASS = 'COMMUNITY_COMPASS',
  /**
   * Special kind of subscription for realtors.
   * GB and Arthur insisted it must be separated from {@link SubscriptionPlans#ENTERPRISE},
   * since realtors have special documents which must be checked as well as special needs.
   */
  REALTOR = 'REALTOR',
  ENTERPRISE = 'ENTERPRISE',
  FULL_ACCESS = 'FULL_ACCESS',
  HOME_SEARCH = 'HOME_SEARCH',
  /** New realtor plan. Don't confuse with deprecated REALTOR. */
  REAL_ESTATE = 'REAL_ESTATE',
  SITE_SELECTION = 'SITE_SELECTION',
  /** Display-only plan; cannot be purchased directly. */
  RESEARCH = 'RESEARCH',
}

export enum ReportTypes {
  COMMUNITY_COMPASS = 'COMMUNITY_COMPASS',
  OPEN_HOUSE_FLYER = 'OPEN_HOUSE_FLYER',
  LOCATION_INTELLIGENCE = 'LOCATION_INTELLIGENCE',
  FEASIBILITY_STUDY = 'FEASIBILITY_STUDY',
  COMPARATIVE_LOCATION = 'COMPARATIVE_LOCATION',
  MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS = 'MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS',
}

/**
 * Every subscription unlocks every indicator on the map
 */
export const ALL_MAP_DATA_INDICATORS: IndicatorsDescription = {
  description: 'Access to all data on the map',
  groups: [
    {
      groupTitle: 'Area wellness',
      bulletList: [
        'Health index',
        'Income index',
        'Wisdom index',
        'Franklin index',
      ],
    },
    {
      groupTitle: 'Demographics',
      bulletList: [
        'Population',
        'Population density',
        'Annual population growth',
        'Household size',
        'Age-sex pyramid',
        'Median age',
        'Marital status',
        'Race & ethnicity',
        'Median household income',
        'Consumer spending',
        'Income distribution',
        'Income Inequality Index',
        'Unemployment rate',
        'Educational attainment',
        'Gender pay gap',
      ],
    },
    {
      groupTitle: 'Housing & real estate',
      bulletList: [
        'Median home value & gross rent',
        'Home value & gross rent trend and distribution',
        'Affordability ratio',
        'Homeownership rate',
        'Median homeowner tenure',
        'Vacancy',
        'Number of rooms',
        'Building age',
      ],
    },
    {
      groupTitle: 'Crime',
      bulletList: [
        'Total crime rate',
        'Community crime rate',
        'Business crime rate',
        'Number of committed crimes by type & location',
      ],
    },
    {
      groupTitle: 'School ranking',
      bulletList: ['Public & private school ranks and details'],
    },
    {
      groupTitle: 'Health related',
      bulletList: [
        'Life expectancy',
        'Uninsured rate',
        'Population-to-medical staff ratios',
      ],
    },
    {
      groupTitle: 'Lifestyle risks',
      bulletList: ['Unhealthy habits index'],
    },
    {
      groupTitle: 'Chronic diseases',
      bulletList: ['Chronic Illness Index'],
    },
    {
      groupTitle: 'Target audience',
      bulletList: ['C-stores', 'Supermarkets', 'Pharmacies', 'Beauty'],
    },
    {
      groupTitle: 'Competitors & nearby amenities',
      bulletList: [
        'Cafe & restaurants',
        'Gas stations',
        'C-stores',
        'Groceries',
        'Car washes',
        'EV chargers',
        'Beauty & spa facilities',
        'Banks, ATMs & financial services',
        'Playgrounds, childcares & parks',
        'Hospitals, clinics & dentists',
        'Retirement facilities',
        'And more',
      ],
    },
    {
      groupTitle: 'Commute & mobility',
      bulletList: [
        'Median commute time',
        'Travel time to work',
        'Time of departure to work',
        'Work commute modes',
        'Household size by vehicles',
        'Number of vehicles available',
      ],
    },
    {
      groupTitle: 'Physical environment',
      bulletList: ['Traffic intensity', 'Road density', 'Air pollution'],
    },
    {
      groupTitle: 'Political landscape',
      bulletList: [
        'Presidential elections 2020 & 2024',
        'House elections 2020',
        'Senate elections 2022',
      ],
    },
  ],
};

/**
 Texts and pricing data for each subscription plan https://docs.google.com/document/d/187R5GUQTvRauJ_CJftGKnJTYktSesDtPlLaDlmcK1LE/
 Feature access table https://docs.google.com/spreadsheets/d/1CJ2uHd4AVTNmAIJzIdTHm9WY7MatiMLXAeDypBfmQ8Q/
 */
export namespace SubscriptionPlans {
  export const description: {
    [subscription in SubscriptionPlans]: SubscriptionPlanDescription;
  } = {
    [SubscriptionPlans.BASIC]: {
      planName: 'Basic',
      requestKey: SubscriptionPlans.BASIC,
      aiTokensIncludedMonthlyText: 'No AI',
      aiTokensIncludedYearlyText: 'No AI',
      pricePackage: '50¢ a day',
      audience: 'For exploration',
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.FEASIBILITY_STUDY,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
        ReportTypes.OPEN_HOUSE_FLYER,
        ReportTypes.COMMUNITY_COMPASS,
      ],
      accessibleFeaturesDescription: {
        reportTypesDescription: {},
        plainTextFeatureDescription: [
          '7 days access',
          'Nationwide (U.S.)',
          // 'High-resolution 1-mile cell data',
        ],
      },
      notAvailableForUser: true,
    },
    [SubscriptionPlans.PLUS]: {
      planName: 'Plus',
      requestKey: SubscriptionPlans.PLUS,
      pricePackage: '$10',
      audience: 'For Solo Analysis',
      includesAi: true,
      aiTokensIncludedMonthlyText: 'Personal AI bundle',
      aiTokensIncludedYearlyText: 'Personal AI bundle',
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.FEASIBILITY_STUDY,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
        ReportTypes.OPEN_HOUSE_FLYER,
        ReportTypes.COMMUNITY_COMPASS,
      ],
      accessibleFeaturesDescription: {
        reportTypesDescription: {},
        plainTextFeatureDescription: [
          '30 days access',
          'Personal AI Map',
          '2 Free Infographic reports',
        ],
      },
      notAvailableForUser: true,
    },
    [SubscriptionPlans.PRO]: {
      planName: 'Pro',
      requestKey: SubscriptionPlans.PRO,
      pricePackage: '$100',
      audience: 'For Advanced  Analysis',
      includesAi: true,
      aiTokensIncludedMonthlyText: 'Advanced AI bundle',
      aiTokensIncludedYearlyText: 'Advanced AI bundle',
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.FEASIBILITY_STUDY,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
        ReportTypes.OPEN_HOUSE_FLYER,
        ReportTypes.COMMUNITY_COMPASS,
      ],
      accessibleFeaturesDescription: {
        reportTypesDescription: {},
        plainTextFeatureDescription: [
          '365 days access',
          'Advanced AI Map',
          '10 Free Infographic reports',
        ],
      },
      notAvailableForUser: true,
    },
    [SubscriptionPlans.KNOW_YOUR_COUNTRY]: {
      notAvailableForUser: true,
      planName: 'Know your country',
      preformattedPlanName: 'Know your country',
      priceYearly: '$49.95',
      priceMonthly: '$4.95',
      priceMonthAnnually: '$59.95',
      yearlyDiscountPercents: '17% OFF',
      audience: 'for educators',
      aiTokensIncludedMonthlyText: '100K ~ only 10 messages',
      aiTokensIncludedYearlyText: '1.5M ~ 150 messages',
      requestKey: SubscriptionPlans.KNOW_YOUR_COUNTRY,
      accessibleReportTypes: [ReportTypes.COMMUNITY_COMPASS],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          'Nationwide search',
          'Up-to-date & accurate data',
        ],
        reportTypesDescription: {
          reportsBase: [],
        },
      },
    },
    [SubscriptionPlans.COMMUNITY_COMPASS]: {
      notAvailableForUser: true,
      planName: 'Community compass',
      preformattedPlanName: 'Community compass',
      priceMonthly: '$7.95',
      priceYearly: '$49.95',
      priceMonthAnnually: '$95.95',
      yearlyDiscountPercents: '52% OFF',
      audience: 'for homebuyers',
      aiTokensIncludedMonthlyText: '1M ~ 100 messages',
      aiTokensIncludedYearlyText: '15M ~ 1500 messages',
      requestKey: SubscriptionPlans.COMMUNITY_COMPASS,
      accessibleReportTypes: [ReportTypes.COMMUNITY_COMPASS],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          'Nationwide search',
          'Up-to-date & accurate data',
          'High-resolution 5-mile and 1-mile cell data',
        ],
        reportTypesDescription: {
          reportsBase: [
            {
              reportsPaidDescription: '$9.95 per Community Compass PDF report',
              includedInSubscription: false,
              sampleURL: COMMUNITY_COMPASS_REPORT_SAMPLE,
              isReadyToOrder: true,
            },
          ],
        },
      },
    },
    [SubscriptionPlans.REALTOR]: {
      notAvailableForUser: true,
      planName: 'Realtors’ Special',
      preformattedPlanName: 'Realtors’ Special',
      priceYearly: '$249.95',
      priceMonthly: '$24.95',
      priceMonthAnnually: '$299.95',
      yearlyDiscountPercents: '17% OFF',
      aiTokensIncludedMonthlyText: '3M ~ 300 messages',
      aiTokensIncludedYearlyText: '40M ~ 4000 messages',
      audience: 'for realtors',
      requestKey: SubscriptionPlans.REALTOR,
      accessibleReportTypes: [
        ReportTypes.COMMUNITY_COMPASS,
        ReportTypes.OPEN_HOUSE_FLYER,
      ],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          'Nationwide search',
          'Up-to-date & accurate data',
          'High-resolution 5-mile and 1-mile data',
        ],
        reportTypesDescription: {
          reportsMonthly: [
            {
              reportsIncludedDescription: '4 free open house flyers',
              reportsPaidDescription: '$4.99 per extra report',
              includedInSubscription: true,
              sampleURL: OPEN_HOUSE_FLYER_REPORT_SAMPLE,
              isReadyToOrder: true,
            },
            {
              reportsPaidDescription: '$9.95 per Community Compass report',
              includedInSubscription: false,
              sampleURL: COMMUNITY_COMPASS_REPORT_SAMPLE,
              isReadyToOrder: true,
            },
          ],
          reportsYearly: [
            {
              reportsIncludedDescription: '48 free open house flyers',
              reportsPaidDescription: '$4.99 per extra report',
              includedInSubscription: true,
              sampleURL: OPEN_HOUSE_FLYER_REPORT_SAMPLE,
              isReadyToOrder: true,
            },
            {
              reportsPaidDescription: '$9.95 per Community Compass report',
              includedInSubscription: false,
              sampleURL: COMMUNITY_COMPASS_REPORT_SAMPLE,
              isReadyToOrder: true,
            },
          ],
          reportsBase: [],
        },
      },
    },
    [SubscriptionPlans.ENTERPRISE]: {
      notAvailableForUser: true,
      planName: 'Location Intelligence',
      preformattedPlanName: 'Location Intelligence',
      priceYearly: '$2,499.95',
      priceMonthly: '$249.95',
      priceMonthAnnually: '$2,999.95',
      aiTokensIncludedMonthlyText: '10M ~ 1000 messages',
      aiTokensIncludedYearlyText: '150M ~ 15000 messages',
      yearlyDiscountPercents: '17% OFF',
      audience: 'for businesses',
      requestKey: SubscriptionPlans.ENTERPRISE,
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
      ],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          'Nationwide search',
          'Up-to-date & accurate data',
          'High-resolution 5-mile and 1-mile data',
        ],
        reportTypesDescription: {
          reportsYearly: [
            {
              reportsIncludedDescription: '7 free location analysis reports',
              includedInSubscription: true,
              reportsPaidDescription: '$399 per extra report',
              sampleURL: LOCATION_INTELLIGENCE_REPORT_SAMPLE,
              isReadyToOrder: false,
            },
            {
              reportsIncludedDescription:
                '1 free comparative location analysis report',
              includedInSubscription: true,
              reportsPaidDescription: '$499 per extra report',
              sampleURL: COMPARATIVE_LOCATION_ANALYSIS_REPORT_SAMPLE,
              isReadyToOrder: false,
            },
          ],
          reportsMonthly: [
            {
              reportsPaidDescription: `7 free location analysis reports\n$399 per extra report`,
              includedInSubscription: false,
              sampleURL: LOCATION_INTELLIGENCE_REPORT_SAMPLE,
              isReadyToOrder: false,
            },
            {
              reportsPaidDescription:
                '1 free comparative location analysis report\n$499 per extra report',
              includedInSubscription: false,
              sampleURL: COMPARATIVE_LOCATION_ANALYSIS_REPORT_SAMPLE,
              isReadyToOrder: false,
            },
          ],
        },
      },
    },

    [SubscriptionPlans.FULL_ACCESS]: {
      planName: 'FULL_ACCESS',
      requestKey: SubscriptionPlans.FULL_ACCESS,
      aiTokensIncludedMonthlyText: '10M',
      aiTokensIncludedYearlyText: '10M',
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.FEASIBILITY_STUDY,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
        ReportTypes.OPEN_HOUSE_FLYER,
        ReportTypes.COMMUNITY_COMPASS,
      ],
      accessibleFeaturesDescription: {
        reportTypesDescription: {},
      },
      notAvailableForUser: true,
    },

    [SubscriptionPlans.HOME_SEARCH]: {
      planName: 'Neighborhood Search',
      requestKey: SubscriptionPlans.HOME_SEARCH,
      audience: 'for choosing where to live',
      priceMonthly: '$79',
      requiresRegion: true,
      includesAi: true,
      aiTokensIncludedMonthlyText: 'Included',
      aiTokensIncludedYearlyText: 'Included',
      accessibleReportTypes: [ReportTypes.COMMUNITY_COMPASS],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          '2 neighborhood reports',
          '1 mile search radius',
          'Neighborhoods comparison',
          'Filter-based area search',
          'Map AI chat for area questions',
        ],
        reportTypesDescription: {},
      },
    },

    [SubscriptionPlans.REAL_ESTATE]: {
      planName: 'Real Estate Suite',
      requestKey: SubscriptionPlans.REAL_ESTATE,
      audience: 'for closing deals faster',
      priceMonthly: '$79',
      requiresRegion: true,
      includesAi: true,
      aiTokensIncludedMonthlyText: 'Included',
      aiTokensIncludedYearlyText: 'Included',
      accessibleReportTypes: [
        ReportTypes.COMMUNITY_COMPASS,
        ReportTypes.OPEN_HOUSE_FLYER,
      ],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          '20 neighborhood reports',
          '1 mile search radius',
          'Neighborhoods comparison',
          'Filter-based area search',
          'Map AI chat for area questions',
        ],
        reportTypesDescription: {},
      },
    },

    [SubscriptionPlans.SITE_SELECTION]: {
      planName: 'Site Selection Suite',
      requestKey: SubscriptionPlans.SITE_SELECTION,
      audience: 'for choosing business locations',
      priceMonthly: '$249',
      requiresRegion: true,
      includesAi: true,
      aiTokensIncludedMonthlyText: 'Included',
      aiTokensIncludedYearlyText: 'Included',
      accessibleReportTypes: [
        ReportTypes.LOCATION_INTELLIGENCE,
        ReportTypes.COMPARATIVE_LOCATION,
        ReportTypes.FEASIBILITY_STUDY,
        ReportTypes.MAP_AI_LOCATION_OVERVIEW_FOR_BEAUTY_SALONS,
      ],
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          'Sales forecast tool',
          'Drive-time trade area',
          'Customizable target market by age and gender',
          'Competitive analysis & market saturation',
          '5-year revenue projection',
          '2 location assessment reports',
          '1 mile search radius',
          'Competitive landscape',
          'Filter-based area search',
          'Map AI chat for area questions',
        ],
        reportTypesDescription: {},
      },
    },

    [SubscriptionPlans.RESEARCH]: {
      planName: 'Academia',
      requestKey: SubscriptionPlans.RESEARCH,
      audience: 'for analysts and data-driven teams',
      contactUsOnly: true,
      aiTokensIncludedMonthlyText: 'Included',
      aiTokensIncludedYearlyText: 'Included',
      accessibleReportTypes: null,
      accessibleFeaturesDescription: {
        plainTextFeatureDescription: [
          '1 mile search radius',
          'API-ready outputs',
          'Filter-based area search',
          'Map AI chat for area questions',
          'Data export to Excel',
        ],
        reportTypesDescription: {},
      },
    },
  };

  /**
   * Teaser set for logged-out visitors: one headline indicator per menu group,
   * enough to make the map worth looking at without answering a real question.
   * MUST stay a subset of {@link trialDescription} - `isBySubscription` reads
   * only the trial list, so anything guest-visible but trial-hidden would be
   * mislabelled "by subscription" to the very users who can already see it.
   */
  export const unauthorizedUser: UnauthorizedDescription = {
    accessibleFeatures: [
      HEALTH_INDEX_CONST,
      HEALTH_INDEX_BY_COUNTRY,
      HEALTH_INDEX_BY_STATE,
      WEALTH_INDEX_CONST,
      WEALTH_INDEX_BY_COUNTRY,
      WEALTH_INDEX_BY_STATE,
      WISE_INDEX_CONST,
      WISE_INDEX_BY_COUNTRY,
      WISE_INDEX_BY_STATE,
      POPULATION,
      MEDIAN_AGE,
      RACE_DISTRIBUTION_CHART,
      MEDIAN_INCOME,
      MEDIAN_HOUSEHOLD_INCOME_TREND,
      LIFE_EXPECTANCY,
      AIR_POLLUTION_INDEX,
      BACHELORS_DEGREE_OR_HIGHER_FRACTION,
      HOME_OWNERSHIP_RATIO,
      CRIME_RATE,
      FAMILY_POI_CHART,
      PRESIDENTIAL_ELECTIONS_RESULTS2020,
      PRESIDENTIAL_ELECTIONS_RESULTS2024,
    ],
  };

  /**
   * Free-after-registration set. Source of truth is the "Map AI parameters"
   * sheet: everything it marks `x` sits behind a subscription, everything it
   * marks `show` is here. Deviations, all deliberate: the sheet omits the
   * political layers, the Franklin index and the National/By-state variants
   * (kept free, as before), and MEDIAN_HOUSEHOLD_INCOME_TREND is `x` in the
   * sheet but stays free for everyone.
   */
  export const trialDescription: TrialDescription = {
    planName: 'trial',
    accessibleReportTypes: [ReportTypes.COMMUNITY_COMPASS],
    accessibleFeatures: [
      HEALTH_INDEX_CONST,
      HEALTH_INDEX_BY_COUNTRY,
      HEALTH_INDEX_BY_STATE,
      WEALTH_INDEX_CONST,
      WEALTH_INDEX_BY_COUNTRY,
      WEALTH_INDEX_BY_STATE,
      WISE_INDEX_CONST,
      WISE_INDEX_BY_COUNTRY,
      WISE_INDEX_BY_STATE,
      POPULATION,
      MEDIAN_AGE,
      RACE_DISTRIBUTION_CHART,
      MEDIAN_INCOME,
      MEDIAN_HOUSEHOLD_INCOME_TREND,
      LIFE_EXPECTANCY,
      AIR_POLLUTION_INDEX,
      BACHELORS_DEGREE_OR_HIGHER_FRACTION,
      HOME_OWNERSHIP_RATIO,
      CRIME_RATE,
      FAMILY_POI_CHART,
      PRESIDENTIAL_ELECTIONS_RESULTS2020,
      PRESIDENTIAL_ELECTIONS_RESULTS2024,
      COMBINED_HWW_CONST,
      COMBINED_HWW_BY_COUNTRY,
      COMBINED_HWW_BY_STATE,
      POPULATION_GROWTH_TREND,
      MARITAL_STATUS_CHART,
      GINI_INDEX,
      PATIENT_TO_PHYSICIAN_RATIO,
      SMOKING_RATE,
      MEDIAN_HOUSING_SALE_COST_TREND,
      HOME_PRICE_TO_INCOME_RATIO,
      AVERAGE_TRAVEL_TIME_TO_WORK,
      TRAFFIC_INTENSITY_CHART,
      HEALTHCARE_POI_CHART,
      BIDEN_VS_TRUMP_2020_POPULATION,
      HARRIS_VS_TRUMP_2024_POPULATION,
      HOUSE_OF_REPRESENTATIVES_ELECTION_RESULTS,
      SENATE_ELECTION_2022_RESULTS,
    ],
    accessibleFeaturesText: [],
  };
}

export class User {
  readonly data: UserPersonalData;

  constructor(data: UserPersonalData) {
    this.data = data;
  }

  public creationDate(): Date {
    return new Date(this.data.createdAt);
  }

  public fullName(): string {
    return `${this.data.firstName} ${this.data.lastName}`;
  }

  public fullNameAndCompany(): string {
    if (this.data.companyName) {
      return `${this.fullName()}, ${this.data.companyName}`;
    } else {
      return this.fullName();
    }
  }
}
